import { buildUnitContentTree, getCourseUnits } from "./contentEditorHelpers";

const normalizeContentItemType = (item) => {
  if (!item || typeof item !== "object") return "";
  const ct = item.content_type || item.type;
  if (ct === "info_page") return "info-page";
  return ct || "";
};

export function reorderItems(items, startIndex, endIndex) {
  const result = Array.from(items);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
}

export function findVersionStepsFromCourse(courseData, versionId) {
  for (const unit of getCourseUnits(courseData)) {
    for (const item of buildUnitContentTree(unit)) {
      if (item.type !== "lesson") continue;
      for (const version of item.versions || []) {
        if (version?.id != null && String(version.id) === String(versionId)) {
          return (version.lesson_steps || []).filter((s) => s?.id != null);
        }
      }
    }
  }
  return [];
}

/**
 * Renumber the course's sessions after a drag, keeping everything inside them.
 *
 * The optimistic handlers take the complete new `units` array and replace the course's with it, so
 * handing them the dragged sessions alone would delete every group in the course from editor state
 * until the next refetch. Each session is followed by the units inside it, unchanged: a group's
 * place is the reference in its parent's content list, which a session drag never touches.
 */
export function applyTopLevelUnitOrder(courseData, reorderedTopLevel) {
  const all = getCourseUnits(courseData);
  const byParent = new Map();
  all.forEach((unit) => {
    if (unit?.parent_unit_id == null) return;
    const key = String(unit.parent_unit_id);
    byParent.set(key, [...(byParent.get(key) || []), unit]);
  });

  const ordered = [];
  const appendSubtree = (unit) => {
    ordered.push(unit);
    (byParent.get(String(unit.id)) || []).forEach(appendSubtree);
  };
  (Array.isArray(reorderedTopLevel) ? reorderedTopLevel : []).forEach((unit, index) => {
    appendSubtree({ ...unit, order: index + 1 });
  });

  // Anything the drag did not mention — a session the sidebar had not loaded — keeps its place
  // rather than vanishing.
  const placed = new Set(ordered.map((unit) => String(unit.id)));
  all.forEach((unit) => {
    if (!placed.has(String(unit.id))) ordered.push(unit);
  });
  return setCourseUnits(courseData, ordered);
}

export function setCourseUnits(courseData, newUnits) {
  if (!courseData) return courseData;
  const { course_units, ...rest } = courseData;
  return { ...rest, units: Array.isArray(newUnits) ? newUnits : [] };
}

/**
 * Patch a content item's per-language activation map into the shell course tree so the sidebar badge
 * (which reads `item.active_by_language` from the cached tree, not the live editor state) reflects a
 * CAS response immediately, without waiting for a full tree refetch. Identity is `(type, id)` — lesson,
 * scenario, and info-page ids come from separate tables and can collide, so id alone would patch the
 * wrong node. Leaves all other nodes untouched; returns the course unchanged when the target is not
 * present or the inputs are missing. `type` accepts either `info_page` or `info-page`.
 */
export function setContentItemActivation(courseData, type, itemId, activeByLanguage) {
  if (!courseData || itemId == null || !activeByLanguage) return courseData;
  const target = String(itemId);
  const targetType = type === "info_page" ? "info-page" : type;
  const units = getCourseUnits(courseData).map((unit) => ({
    ...unit,
    content_items: (unit.content_items || []).map((item) =>
      item?.id != null &&
      String(item.id) === target &&
      normalizeContentItemType(item) === targetType
        ? { ...item, active_by_language: { ...activeByLanguage } }
        : item
    ),
  }));
  return setCourseUnits(courseData, units);
}

export function mapCourseVersions(courseData, versionId, mapFn) {
  const mapItems = (items) =>
    items.map((item) => {
      if (!item.versions) return item;
      const hasTarget = item.versions.some(
        (v) => v?.id != null && String(v.id) === String(versionId)
      );
      if (!hasTarget) return item;
      return { ...item, versions: item.versions.map((v) =>
        v?.id != null && String(v.id) === String(versionId) ? mapFn(v) : v
      ) };
    });
  const units = getCourseUnits(courseData).map((u) => ({
    ...u,
    content_items: mapItems(u.content_items || []),
  }));
  return setCourseUnits(courseData, units);
}

/**
 * After a lesson step is deleted, keep the shell course tree in sync so the sidebar outline updates
 * without a full refresh. Uses the same `versions` / `lesson_steps` shape as {@link mapCourseVersions}.
 */
export function removeLessonStepFromCourseData(courseData, versionId, stepId) {
  if (!courseData || versionId == null || stepId == null) return courseData;
  const sid = String(stepId);
  return mapCourseVersions(courseData, versionId, (v) => {
    const snake = Array.isArray(v.lesson_steps) ? v.lesson_steps : [];
    const camel = Array.isArray(v.lessonSteps) ? v.lessonSteps : null;
    const filter = (arr) =>
      (Array.isArray(arr) ? arr : []).filter((s) => s?.id == null || String(s.id) !== sid);
    const next = { ...v, lesson_steps: filter(snake) };
    if (camel != null) {
      next.lessonSteps = filter(camel);
    }
    return next;
  });
}

/** Remove a lesson version from the nested program tree (sidebar). */
export function removeLessonVersionFromCourseData(courseData, lessonId, versionId) {
  if (!courseData || lessonId == null || versionId == null) return courseData;
  const lid = String(lessonId);
  const vid = String(versionId);
  const units = getCourseUnits(courseData).map((unit) => ({
    ...unit,
    content_items: (unit.content_items || []).map((item) => {
      if (normalizeContentItemType(item) !== "lesson" || item?.id == null || String(item.id) !== lid) {
        return item;
      }
      const versions = Array.isArray(item.versions) ? item.versions : [];
      return {
        ...item,
        versions: versions.filter((v) => v?.id == null || String(v.id) !== vid),
      };
    }),
  }));
  return setCourseUnits(courseData, units);
}

/** Remove a lesson content item from its parent unit in the program tree (sidebar). */
export function removeLessonFromCourseData(courseData, lessonId) {
  if (!courseData || lessonId == null) return courseData;
  const lid = String(lessonId);
  const units = getCourseUnits(courseData).map((unit) => ({
    ...unit,
    content_items: (unit.content_items || []).filter(
      (item) =>
        !(
          normalizeContentItemType(item) === "lesson" &&
          item?.id != null &&
          String(item.id) === lid
        )
    ),
  }));
  return setCourseUnits(courseData, units);
}

export function mapCourseStepCoachContent(courseData, stepId, newCoachContent) {
  const mapStep = (s) =>
    s?.id != null && String(s.id) === String(stepId)
      ? { ...s, coach_content: newCoachContent }
      : s;
  const mapItems = (items) =>
    items.map((item) => {
      if (!item.versions) return item;
      return {
        ...item,
        versions: item.versions.map((v) => ({
          ...v,
          lesson_steps: (v.lesson_steps || []).map(mapStep),
        })),
      };
    });
  const units = getCourseUnits(courseData).map((u) => ({
    ...u,
    content_items: mapItems(u.content_items || []),
  }));
  return setCourseUnits(courseData, units);
}

export function findStepFromCourse(courseData, stepId) {
  for (const unit of getCourseUnits(courseData)) {
    for (const item of buildUnitContentTree(unit)) {
      if (item.type !== "lesson") continue;
      for (const version of item.versions || []) {
        for (const step of version.lesson_steps || []) {
          if (step?.id != null && String(step.id) === String(stepId)) {
            return step;
          }
        }
      }
    }
  }
  return null;
}

/**
 * Lesson editor routes sync `lessonVersions` from the page only after local state matches the URL.
 * During that gap the shell still has the program tree on `course`; use it so the sidebar does not
 * flash empty/collapsed like program/unit navigation (which always reads versions from the tree).
 */
export function findLessonVersionsFromCourse(courseData, lessonId) {
  if (!courseData || lessonId == null) return [];
  for (const unit of getCourseUnits(courseData)) {
    for (const item of buildUnitContentTree(unit)) {
      if (item.type !== "lesson" || item?.id == null) continue;
      if (String(item.id) !== String(lessonId)) continue;
      const versions = item.versions;
      if (!Array.isArray(versions)) return [];
      return versions.map((v) => {
        const steps = Array.isArray(v?.lessonSteps)
          ? v.lessonSteps
          : Array.isArray(v?.lesson_steps)
            ? v.lesson_steps
            : [];
        return {
          ...v,
          lessonSteps: steps,
          lesson_steps: v?.lesson_steps || steps,
        };
      });
    }
  }
  return [];
}
